import { useState } from 'react';
import { message } from 'antd';
import { useModel } from '@umijs/max';
import { changeCallbackStatus } from '@/services/app/tenant/call-queue';
import { QueueItem } from '@/contexts/CallbackQueueContext';
import { globalMakeCallRef } from '@/components/CallWidget/TwilioDeviceContext';
import { watchCallbackCall } from './postCallPrompt';

/**
 * The two pending-callback actions (Call Back / Dismiss) shared by the nav badge popover
 * and the site-wide banner, so both surfaces change status the exact same way. The
 * status write goes through changeCallbackStatus (our backend records it, then forwards
 * to Appointment Center); the next WS push refreshes every open tab's queue.
 *
 * Call Back marks the row in_progress for the duration of the call. What happens after
 * is automatic: an unanswered dial is re-queued by the backend from Twilio's customer-leg
 * status, and a connected call ends with the post-call prompt (see postCallPrompt.ts)
 * asking the CSR whether it was resolved or needs another try.
 *
 * `updatingId` is the row currently being acted on — callers disable the other rows'
 * buttons while it is set so a CSR can't fire two actions at once.
 */
export function useCallbackActions(opts?: { onCallBackStarted?: () => void }) {
  const { initialState } = useModel('@@initialState');
  const [updatingId, setUpdatingId] = useState<number | null>(null);

  const callBack = async (item: QueueItem) => {
    const makeCall = globalMakeCallRef.current;
    if (!makeCall) {
      message.error('Phone service not ready. Please wait or refresh.');
      return;
    }
    const csrId = initialState?.currentUser?.id;
    setUpdatingId(item.id);
    try {
      const call = await makeCall(item.customer_phone, item.external_id ? Number(item.external_id) : undefined);
      // makeCall reports its own failures (no device, no assigned number, dial error) and
      // returns undefined — leave the row pending so it can simply be retried.
      if (!call) return;
      await changeCallbackStatus(item, 'in_progress', csrId ?? '');
      watchCallbackCall(call, item);
      opts?.onCallBackStarted?.();
    } catch {
      message.error('Failed to start callback');
    } finally {
      setUpdatingId(null);
    }
  };

  const dismiss = async (item: QueueItem) => {
    const csrId = initialState?.currentUser?.id;
    setUpdatingId(item.id);
    try {
      // Appointment Center first (who dismissed), then record on our backend.
      const { apptUpdated } = await changeCallbackStatus(item, 'dismissed', csrId ?? '', 'Dismissed from Dispatch');
      if (!item.external_id) {
        message.warning('Dismissed — this callback has no Appointment Center id, so it was not synced there');
      } else if (!apptUpdated) {
        message.warning('Dismissed — but the Appointment Center update failed');
      } else {
        message.success('Dismissed');
      }
      // The next WS push / fetch refreshes the queue.
    } catch {
      message.error('Failed to dismiss');
    } finally {
      setUpdatingId(null);
    }
  };

  return { updatingId, callBack, dismiss };
}
